import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flame } from "lucide-react";

const Wall = () => {
    const [intentions, setIntentions] = useState([]);
    const [text, setText] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const load = () => api.get("/intentions").then(r => setIntentions(r.data));
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!text.trim()) return;
        setSubmitting(true);
        try {
            const r = await api.post("/intentions", { intention: text.trim(), is_public: isPublic });
            toast.success("We will pray with you.", { description: r.data.message });
            setText("");
            load();
        } catch { toast.error("Something went awry"); }
        finally { setSubmitting(false); }
    };

    const pray = async (id) => {
        try {
            const r = await api.post(`/intentions/${id}/pray`);
            setIntentions(prev => prev.map(i => i.id === id ? { ...i, prayed_count: r.data.prayed_count } : i));
        } catch {}
    };

    return (
        <div className="relative" data-testid="wall-page">
            <div className="relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-0">
                    <img src="https://images.unsplash.com/photo-1765146567664-cf0c0d987da9?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-background/60" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
                    <div className="small-caps text-xs text-muted-foreground mb-3">A wall of quiet candles</div>
                    <h1 className="font-serif text-4xl md:text-5xl">The Prayer Wall</h1>
                    <p className="mt-6 max-w-xl mx-auto text-muted-foreground italic">
                        Leave your intention. Your fellow children of Abba Father will carry it in prayer.
                    </p>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-16">
                <div className="missal-card p-8 md:p-10 mb-14">
                    <div className="small-caps text-[11px] accent-liturgy mb-3">Submit an intention</div>
                    <textarea value={text} onChange={e => setText(e.target.value)} data-testid="intention-input"
                        rows={4} placeholder="A person, a struggle, a thanksgiving…"
                        className="w-full bg-transparent border border-border p-4 text-[15px] font-body focus:outline-none focus:border-liturgy" />
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
                        <label className="flex items-center gap-3 text-sm text-muted-foreground">
                            <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} data-testid="intention-public" />
                            <span>Post to the wall (anonymous). Uncheck to keep private.</span>
                        </label>
                        <button onClick={submit} disabled={submitting || !text.trim()} data-testid="intention-submit"
                            className="px-6 py-3 bg-liturgy text-white text-sm small-caps disabled:opacity-40">
                            {submitting ? "Sending…" : "We will pray with you"}
                        </button>
                    </div>
                </div>

                <div className="divider-ornament small-caps text-xs mb-8">Intentions being carried</div>

                <div className="columns-1 md:columns-2 gap-6 space-y-6" data-testid="intentions-list">
                    {intentions.map(i => (
                        <div key={i.id} className="missal-card p-6 break-inside-avoid" data-testid={`intention-${i.id}`}>
                            <p className="font-body leading-relaxed text-[15px] italic">“{i.intention}”</p>
                            <div className="mt-4 flex items-center justify-between">
                                <span className="small-caps text-[10px] text-muted-foreground">— {i.author_name || "Anonymous"}</span>
                                <button onClick={() => pray(i.id)} data-testid={`pray-${i.id}`}
                                    className="flex items-center gap-2 text-xs accent-liturgy hover:opacity-70 transition-opacity">
                                    <Flame size={14} className="animate-flicker" />
                                    <span data-testid={`prayed-count-${i.id}`}>{i.prayed_count} prayed</span>
                                </button>
                            </div>
                        </div>
                    ))}
                    {intentions.length === 0 && <div className="text-center text-muted-foreground py-12">No public intentions yet. Be the first candle lit.</div>}
                </div>
            </div>
        </div>
    );
};

export default Wall;
